/* Copyright (c) 2020 Pej AB. All rights reserved.
 * Created by Marcus Johansson on 2019-08-26. */

import firebase from 'firebase/app';
import {Observable} from 'rxjs';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {PejFirestoreService} from '../core/firestore';
import {DeliveryPrice} from '../models/delivery-price';
import {pejPlaceMapFilterRoot, PlaceSpec} from '../models/place-spec';
import {FirestoreMap} from '../models/shared/firestore-map';
import {firestoreQueryListener} from '../utils/firestore';
import {shareCached} from '../utils/rx';
import {trackingArrayEquals} from '../utils/tracking';
import Query = firebase.firestore.Query;

const cacheKeyForShop = (shopId) => `forShop:${shopId}`;

function fsPlaces(shopId: number): Query {
    return PejFirestoreService.firestore()
        .collection('shops').doc(`${shopId}`)
        .collection('places')
        .orderBy('ordering', 'asc');
}

export class PejPlaceFirestoreServiceClass {
    private observableCache: { [id: string]: Observable<any> } = {};

    public forShop(shopId: number): Observable<FirestoreMap<PlaceSpec>> {
        return shareCached(this.observableCache, cacheKeyForShop(shopId), () =>
            firestoreQueryListener(fsPlaces(shopId), PlaceSpec));
    }

    public forShopOnline(shopId: number): Observable<FirestoreMap<PlaceSpec>> {
        return this.forShop(shopId).pipe(filter(obj => !obj?.fromCache));
    }

    public places(shopId: number, ids: string[]): Observable<PlaceSpec[]> {
        return this.forShop(shopId).pipe(
            map((m) => ids.map(id => m.get(id))),
            distinctUntilChanged((prev, curr) => trackingArrayEquals(prev, curr))
        );
    }

    public pricing(shopId: number, ids: string[]): Observable<DeliveryPrice[]> {
        return this.places(shopId, ids).pipe(map(places =>
            // (PlaceSpec child settings override parent settings)
            // Find the last child PlaceSpec with pricing set
            places
                .filter(place => place != null && place.pricing != null && place.pricing.length > 0)
                .reduce((previousValue: DeliveryPrice[], currentValue) => currentValue.pricing, [])
        ));
    }

    public listRoot(shopId: number): Observable<PlaceSpec[]> {
        return this.forShop(shopId).pipe(
            map((m) => pejPlaceMapFilterRoot(m)),
            distinctUntilChanged((prev, curr) => trackingArrayEquals(prev, curr))
        );
    }
}


export const PejPlaceFirestoreService = Object.seal(new PejPlaceFirestoreServiceClass());
